import React, { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Check, ClipboardList, Factory, List, Plus, X } from 'lucide-react';
import { Heading } from '../layout';
import { ScheduleCalendar, TabNavigation } from '../reusable';
import { FormDrawer } from '../forms';
import { useUIColors } from '../../hooks/useTheme';
import ProductionDataTable from './ProductionDataTable';
import ProductionStatusChip from './ProductionStatusChip';
import ProductRequestRejectModal from './ProductRequestRejectModal';
import ProductRequestViewModal from './ProductRequestViewModal';
import useProductionStore from './useProductionStore';
import {
  getBatchStatusColor,
  emptyBatch,
  formatDateEs,
  formatQty,
  FORMULA_KIND_BASE,
  FORMULA_KIND_COLOR,
  nextBatchCode,
  PAINT_PRODUCT_OPTIONS,
  productLabel,
  RESPONSIBLE_OPTIONS,
  WAREHOUSE_OPTIONS,
} from './productionDomain';
import {
  emptyProductRequest,
  formatContainerBreakdown,
  formatDeadline,
  getRequestStatusColor,
  REQUEST_STATUS,
} from './planningDomain';
import { formatRequestQty } from './commercialRequestsUtils';
import { useCommercialCatalogRefresh } from '../../hooks/useCommercialCatalogRefresh';

const cell = (children, align = 'left') => (
  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: align }}>{children}</td>
);

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 36,
  padding: '0 12px',
  borderRadius: 8,
  border: 'none',
  background: '#16a34a',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};

const iconBtn = (bg, enabled = true) => ({
  ...primaryBtn,
  height: 28,
  width: 28,
  padding: 0,
  justifyContent: 'center',
  background: bg,
  opacity: enabled ? 1 : 0.35,
  cursor: enabled ? 'pointer' : 'not-allowed',
});

const canAcceptRequest = (row) =>
  [REQUEST_STATUS.requested, REQUEST_STATUS.rescheduling].includes(row?.status);
const canRejectRequest = (row) =>
  [REQUEST_STATUS.requested, REQUEST_STATUS.planned, REQUEST_STATUS.rescheduling].includes(row?.status) &&
  !row?.op_code;
const canRescheduleRequest = (row) =>
  [REQUEST_STATUS.requested, REQUEST_STATUS.planned, REQUEST_STATUS.inProcess, REQUEST_STATUS.rescheduling].includes(
    row?.status
  );
const isRequestClosed = (row) =>
  [
    REQUEST_STATUS.finished,
    REQUEST_STATUS.delivered,
    REQUEST_STATUS.rejected,
    REQUEST_STATUS.cancelled,
  ].includes(row?.status);

const PaintPlanningWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const catalogTick = useCommercialCatalogRefresh();
  const {
    requests,
    requestItems,
    requestsLoading,
    requestsError,
    batches,
    formulas,
    updateProductRequestPlanning,
    updateProductRequestItemProgress,
    persistBatches,
    refresh,
  } = useProductionStore();
  const [detailFormOpen, setDetailFormOpen] = useState(false);
  const [viewRequestId, setViewRequestId] = useState(null);
  const [rejectRequestRow, setRejectRequestRow] = useState(null);
  const [orderFormOpen, setOrderFormOpen] = useState(false);
  const [detailForm, setDetailForm] = useState(emptyProductRequest());
  const [orderForm, setOrderForm] = useState(emptyBatch());
  const [formError, setFormError] = useState('');
  const [isSavingDetail, setIsSavingDetail] = useState(false);
  const [requestsView, setRequestsView] = useState('requests');
  const [itemsRequestCode, setItemsRequestCode] = useState('');
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);
  const [calendarCollapsed, setCalendarCollapsed] = useState(false);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const colorFormulas = useMemo(
    () => formulas.filter((row) => row.formula_kind === FORMULA_KIND_COLOR && row.status === 'Activa'),
    [formulas]
  );
  const baseFormulas = useMemo(
    () => formulas.filter((row) => row.formula_kind === FORMULA_KIND_BASE && row.status === 'Activa'),
    [formulas]
  );

  const commercialRequests = useMemo(
    () => requests.filter((row) => String(row.origin || '').trim() === 'Comercial'),
    [requests]
  );

  const calendarEvents = useMemo(() => {
    // catalogTick fuerza re-lectura de colores cuando cambian Ajustes de Producción.
    void catalogTick;
    return requests.map((row) => {
      const productsLabel =
        row.items_count > 1
          ? `${row.items_count} productos`
          : row.product_name || row.product_code || '';
      return {
        id: row.id,
        date: row.needed_by,
        title: `${row.sale_code || row.quotation_code || row.code} · ${row.organization_name || 'Cliente'}`,
        subtitle: [row.needed_by_time, row.code, productsLabel].filter(Boolean).join(' · '),
        status: [row.status, row.executive || row.created_by].filter(Boolean).join(' · '),
        color: getRequestStatusColor(row.status),
        row,
      };
    });
  }, [requests, catalogTick]);

  const requestTabs = useMemo(
    () => [
      { key: 'requests', label: 'Solicitudes', count: requests.length },
      { key: 'items', label: 'Ítems', count: requestItems.length },
    ],
    [requests.length, requestItems.length]
  );

  const visibleRequestItems = useMemo(
    () => (itemsRequestCode ? requestItems.filter((row) => row.code === itemsRequestCode) : requestItems),
    [requestItems, itemsRequestCode]
  );

  const openRequestItems = (row) => {
    setItemsRequestCode(row?.code || '');
    setRequestsView('items');
  };

  const requestsSubtitle = requestsLoading
    ? 'Cargando…'
    : requestsError ||
      `${requests.length} solicitudes · ${commercialRequests.length} desde cierre comercial`;

  const itemsSubtitle = requestsLoading
    ? 'Cargando…'
    : requestsError ||
      (itemsRequestCode
        ? `${visibleRequestItems.length} ítems de ${itemsRequestCode}`
        : `${requestItems.length} ítems solicitados`);

  const viewRequest = useMemo(
    () => requests.find((row) => row.request_id === viewRequestId) || null,
    [requests, viewRequestId]
  );
  const viewRequestItems = useMemo(
    () => requestItems.filter((row) => row.request_id === viewRequestId),
    [requestItems, viewRequestId]
  );

  const openView = (row) => {
    if (row?.request_id) setViewRequestId(row.request_id);
  };

  const openReschedule = (row) => {
    if (!row?.request_id || !canRescheduleRequest(row)) return;
    setDetailForm({
      ...emptyProductRequest(),
      id: row.request_id,
      code: row.code || '',
      executive: row.executive || row.created_by || '',
      sale_code: row.sale_code || '',
      status: row.status || '',
      original_needed_by: row.needed_by || '',
      needed_by: row.needed_by || '',
      needed_by_time: row.needed_by_time || '',
      reschedule_reason: '',
      notes: row.notes || '',
    });
    setFormError('');
    setDetailFormOpen(true);
  };

  const acceptRequest = async (row) => {
    if (!row?.request_id || !canAcceptRequest(row)) return;
    try {
      await updateProductRequestPlanning(row.request_id, { status: REQUEST_STATUS.planned });
    } catch (error) {
      window.alert(error?.message || 'No se pudo aceptar la solicitud.');
    }
  };

  const rejectRequest = (row) => {
    if (!row?.request_id || !canRejectRequest(row)) return;
    setRejectRequestRow(row);
  };

  const confirmReject = async ({ reason, notes }) => {
    const row = rejectRequestRow;
    if (!row?.request_id) return;
    // Cancelar: solicitud Cancelada + entrega En Reprogramación (nueva solicitud para reordenar).
    await updateProductRequestPlanning(row.request_id, {
      status: REQUEST_STATUS.cancelled,
      rejection_reason: reason,
      notes: notes ? [String(row.notes || '').trim(), notes].filter(Boolean).join('\n') : undefined,
    });
    setRejectRequestRow(null);
  };

  const saveDetail = async () => {
    if (!detailForm.id) return;
    setIsSavingDetail(true);
    setFormError('');
    try {
      if (!detailForm.needed_by) {
        setFormError('Indica la nueva fecha límite.');
        return;
      }
      const goingToReschedule = detailForm.status !== REQUEST_STATUS.inProcess;
      if (goingToReschedule && !String(detailForm.reschedule_reason || '').trim()) {
        setFormError('Indica el motivo de la reprogramación.');
        return;
      }
      await updateProductRequestPlanning(detailForm.id, {
        ...(goingToReschedule ? { status: REQUEST_STATUS.rescheduling } : {}),
        needed_by_date: detailForm.needed_by,
        needed_by_time: detailForm.needed_by_time || '',
        notes: detailForm.notes || '',
        reschedule_reason: String(detailForm.reschedule_reason || '').trim(),
      });
      setDetailFormOpen(false);
    } catch (error) {
      setFormError(error?.message || 'No se pudo guardar la solicitud.');
    } finally {
      setIsSavingDetail(false);
    }
  };

  const openOrder = (fromRequest) => {
    const formulaKind = fromRequest?.formula_kind === FORMULA_KIND_BASE ? FORMULA_KIND_BASE : FORMULA_KIND_COLOR;
    const formulaPool = formulaKind === FORMULA_KIND_BASE ? baseFormulas : colorFormulas;
    const formula =
      formulaPool.find((row) => row.product_code === fromRequest?.product_code) || formulaPool[0];
    setOrderForm({
      ...emptyBatch(),
      code: nextBatchCode(batches),
      request_code: fromRequest?.code || '',
      request_id: fromRequest?.request_id || null,
      product_code: fromRequest?.product_code || '',
      product_name: fromRequest?.product_name || '',
      color_code: fromRequest?.color_code || '',
      formula_kind: formulaKind,
      formula_id: formula?.id || '',
      formula_code: formula?.code || '',
      planned_qty: fromRequest?.quantity || 100,
      unit: fromRequest?.unit || 'L',
      planned_date: fromRequest?.needed_by || emptyBatch().planned_date,
      status: 'Planificada',
      process_stage: 'planning',
    });
    setFormError('');
    setOrderFormOpen(true);
  };

  const saveOrder = () => {
    if (!orderForm.product_code) {
      setFormError('Selecciona el producto de la orden.');
      return;
    }
    const code = orderForm.code || nextBatchCode(batches);
    persistBatches((current) => {
      const id = current.reduce((max, row) => Math.max(max, Number(row.id) || 0), 0) + 1;
      return [
        ...current,
        {
          ...emptyBatch(),
          ...orderForm,
          id,
          code,
          formula_id: Number(orderForm.formula_id) || null,
          status: 'Planificada',
          process_stage: 'planning',
          events: [
            {
              status: 'Planificada',
              at: new Date().toISOString().slice(0, 10),
              note: 'OP creada desde planificación',
            },
          ],
        },
      ];
    });
    const requestId =
      orderForm.request_id || requests.find((row) => row.code === orderForm.request_code)?.request_id;
    if (requestId) {
      updateProductRequestPlanning(requestId, { status: REQUEST_STATUS.planned, op_code: code }).catch((error) =>
        console.error('No se pudo actualizar la solicitud:', error)
      );
    }
    setOrderFormOpen(false);
  };

  const detailSections = [
    {
      key: 'request',
      title: 'Reprogramar',
      columns: 2,
      fields: [
        { key: 'code', label: 'Código', readOnly: true, disabled: true },
        { key: 'sale_code', label: 'Venta', readOnly: true, disabled: true },
        { key: 'executive', label: 'Solicitado por', readOnly: true, disabled: true },
        { key: 'status', label: 'Estado', readOnly: true, disabled: true },
        { key: 'original_needed_by', label: 'Fecha original', type: 'date', readOnly: true, disabled: true },
        { key: 'needed_by', label: 'Nueva fecha límite', type: 'date' },
        { key: 'needed_by_time', label: 'Hora límite', type: 'time' },
        {
          key: 'reschedule_reason',
          label: 'Motivo de reprogramación',
          type: 'textarea',
          fullWidth: true,
          rows: 2,
        },
        { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true, rows: 2 },
      ],
    },
  ];

  const formulaOptions =
    orderForm.formula_kind === FORMULA_KIND_BASE ? baseFormulas : colorFormulas;

  const orderSections = [
    {
      key: 'order',
      title: 'Orden de producción',
      columns: 2,
      fields: [
        { key: 'code', label: 'Nº OP', readOnly: true },
        { key: 'request_code', label: 'Solicitud' },
        {
          key: 'product_code',
          label: 'Producto',
          type: 'select',
          options: [{ value: '', label: 'Seleccionar producto' }, ...PAINT_PRODUCT_OPTIONS],
        },
        {
          key: 'formula_id',
          label: 'Fórmula',
          type: 'select',
          options: [
            { value: '', label: 'Seleccionar fórmula activa' },
            ...formulaOptions.map((row) => ({
              value: String(row.id),
              label: `${row.code} · ${row.color_code || row.product_name}`,
            })),
          ],
        },
        { key: 'planned_qty', label: 'Cantidad planificada', type: 'number' },
        { key: 'planned_date', label: 'Fecha plan', type: 'date' },
        {
          key: 'warehouse',
          label: 'Bodega',
          type: 'select',
          options: WAREHOUSE_OPTIONS.map((item) => ({ value: item, label: item })),
        },
        {
          key: 'responsible',
          label: 'Responsable',
          type: 'select',
          options: RESPONSIBLE_OPTIONS.map((item) => ({ value: item, label: item })),
        },
        { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true, rows: 2 },
      ],
    },
  ];

  const handleDetailChange = (key, value) => {
    setDetailForm((current) => ({ ...current, [key]: value }));
  };

  const handleOrderChange = (key, value) => {
    setOrderForm((current) => {
      const next = { ...current, [key]: value };
      if (key === 'product_code') next.product_name = productLabel(value);
      if (key === 'formula_id') {
        const formula = formulas.find((row) => String(row.id) === String(value));
        next.formula_code = formula?.code || '';
        next.formula_kind = formula?.formula_kind || current.formula_kind;
        if (formula?.product_code) {
          next.product_code = formula.product_code;
          next.product_name = formula.product_name;
        }
        next.color_code = formula?.color_code || '';
      }
      return next;
    });
  };

  const requestFilters = [
    { key: 'status', label: 'Estado' },
    {
      key: 'executive',
      label: 'Ejecutivo',
      getValue: (row) => row.executive || row.created_by || '',
    },
  ];

  const actionCell = (row) => {
    const actions = [
      { key: 'items', label: 'Ver ítems', icon: <List size={14} />, color: '#0ea5e9', enabled: true, run: openRequestItems },
      { key: 'accept', label: 'Aceptar', icon: <Check size={14} />, color: '#16a34a', enabled: canAcceptRequest(row), run: acceptRequest },
      { key: 'reject', label: 'Cancelar', icon: <X size={14} />, color: '#dc2626', enabled: canRejectRequest(row), run: rejectRequest },
      {
        key: 'reschedule',
        label: 'Reprogramar',
        icon: <CalendarClock size={14} />,
        color: '#64748b',
        enabled: canRescheduleRequest(row),
        run: openReschedule,
      },
    ];
    return (
      <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
        {actions.map((action) => (
          <button
            key={action.key}
            type="button"
            title={action.label}
            aria-label={`${action.label} ${row.code}`}
            disabled={!action.enabled}
            onClick={(event) => {
              event.stopPropagation();
              action.run(row);
            }}
            style={iconBtn(action.color, action.enabled)}
          >
            {action.icon}
          </button>
        ))}
      </div>
    );
  };

  const codeLink = (row, onClick) => (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick(row);
      }}
      style={{
        appearance: 'none',
        border: 'none',
        background: 'transparent',
        padding: 0,
        color: '#0ea5e9',
        fontWeight: 700,
        cursor: 'pointer',
        fontSize: 13,
      }}
    >
      {row.code}
    </button>
  );

  const tabs = [
    {
      key: 'requests',
      label: 'Solicitud de producto',
      icon: <ClipboardList size={16} />,
      content: (
        <>
        <ScheduleCalendar
          events={calendarEvents}
          isDarkMode={isDarkMode}
          title="Calendario de solicitudes"
          allowedViews={['week', 'biweek']}
          defaultView="week"
          emptyLabel="Sin solicitudes"
          collapsed={calendarCollapsed}
          onToggleCollapsed={() => setCalendarCollapsed((prev) => !prev)}
          onEventClick={(evt) => openView(evt.row)}
        />
        {requestsView === 'items' ? (
          <ProductionDataTable
            title="Solicitudes de producto"
            subtitle={itemsSubtitle}
            rows={visibleRequestItems}
            defaultSortKey="deadline_key"
            defaultSortDir="desc"
            searchPlaceholder="Buscar solicitud, venta, producto, color…"
            tabs={requestTabs}
            activeTab={requestsView}
            onTabChange={setRequestsView}
            filters={requestFilters}
            action={
              itemsRequestCode ? (
                <button
                  type="button"
                  onClick={() => setItemsRequestCode('')}
                  style={{
                    ...primaryBtn,
                    background: 'transparent',
                    color: ui.text,
                    border: `1px solid ${ui.border}`,
                  }}
                >
                  Ver todos los ítems
                </button>
              ) : null
            }
            columns={[
              { key: 'Solicitud', sortKey: 'code' },
              { key: 'Origen', sortKey: 'executive' },
              { key: 'Producto', sortKey: 'product_name' },
              { key: 'Color', sortKey: 'color_code' },
              { key: 'Cantidad', sortKey: 'quantity', align: 'right' },
              { key: 'Fecha límite', sortKey: 'deadline_key' },
              { key: 'Estado', sortKey: 'status', align: 'center' },
              { key: '', sortKey: null, align: 'right' },
            ]}
            renderCells={(row) => (
              <>
                {cell(codeLink(row, openView))}
                {cell(row.executive || row.created_by || '—')}
                {cell([row.product_code, row.product_name].filter(Boolean).join(' · ') || '—')}
                {cell(
                  [row.color_code, row.color_name].filter(Boolean).join(' · ') ||
                    (row.formula_kind === FORMULA_KIND_BASE ? 'Base' : '—')
                )}
                {cell(formatRequestQty(row.quantity, row.unit), 'right')}
                {cell(formatDeadline(row.needed_by, row.needed_by_time))}
                {cell(
                  <ProductionStatusChip status={row.status} color={getRequestStatusColor(row.status)} />,
                  'center'
                )}
                {cell(
                  row.op_code || isRequestClosed(row) ? (
                    '—'
                  ) : (
                    <button
                      type="button"
                      onClick={() => openOrder(row)}
                      style={{ ...primaryBtn, height: 28, fontSize: 11 }}
                    >
                      Crear OP
                    </button>
                  ),
                  'right'
                )}
              </>
            )}
          />
        ) : (
          <ProductionDataTable
            title="Solicitudes de producto"
            subtitle={requestsSubtitle}
            rows={requests}
            defaultSortKey="deadline_key"
            defaultSortDir="desc"
            searchPlaceholder="Buscar solicitud, venta, ejecutivo…"
            tabs={requestTabs}
            activeTab={requestsView}
            onTabChange={setRequestsView}
            selectable
            selectedIds={selectedRequestIds}
            onSelectionChange={setSelectedRequestIds}
            getRowId={(row) => row.id}
            getRowAccent={(row) => getRequestStatusColor(row.status)}
            filters={requestFilters}
            columns={[
              { key: 'Código', sortKey: 'code' },
              { key: 'Solicitado por', sortKey: 'executive' },
              { key: 'Fecha límite', sortKey: 'deadline_key' },
              { key: 'Estado', sortKey: 'status', align: 'center' },
              { key: 'Productos', sortKey: 'items_count', align: 'right' },
              { key: '¼ gal / gal / T16 / T20', sortKey: 'containers_total', align: 'right' },
              { key: 'Notas', sortKey: 'particular_note', align: 'center' },
              { key: 'Acciones', sortKey: null, align: 'right' },
            ]}
            renderCells={(row) => (
              <>
                {cell(
                  <div>
                    {codeLink(row, openView)}
                    <div style={{ fontSize: 11, color: ui.muted, marginTop: 2 }}>
                      {[row.sale_code, row.organization_name].filter(Boolean).join(' · ') || row.origin}
                    </div>
                  </div>
                )}
                {cell(row.executive || row.created_by || '—')}
                {cell(<span style={{ whiteSpace: 'nowrap' }}>{formatDeadline(row.needed_by, row.needed_by_time)}</span>)}
                {cell(
                  <ProductionStatusChip status={row.status} color={getRequestStatusColor(row.status)} />,
                  'center'
                )}
                {cell(row.items_count, 'right')}
                {cell(
                  <span
                    title={
                      row.qty_other_formats
                        ? `Además ${row.qty_other_formats} envases en otros formatos (ver ítems)`
                        : '¼ galón / galón / tineta 16 L / tineta 20 L'
                    }
                    style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                  >
                    {formatContainerBreakdown(row)}
                    {row.qty_other_formats ? <span style={{ color: ui.muted }}> +otros</span> : null}
                  </span>,
                  'right'
                )}
                {cell(
                  row.particular_note ? (
                    <span
                      title={row.particular_note}
                      aria-label={`Nota: ${row.particular_note}`}
                      style={{ color: '#d97706', fontWeight: 800, fontSize: 16, cursor: 'help' }}
                    >
                      *
                    </span>
                  ) : (
                    ''
                  ),
                  'center'
                )}
                {cell(actionCell(row), 'right')}
              </>
            )}
          />
        )}
        </>
      ),
    },
    {
      key: 'orders',
      label: 'Órdenes de producción',
      icon: <Factory size={16} />,
      content: (
        <ProductionDataTable
          title="Órdenes de producción"
          subtitle={`${batches.length} órdenes`}
          rows={batches}
          defaultSortKey="planned_date"
          searchPlaceholder="Buscar OP, producto, fórmula, solicitud…"
          action={
            <button type="button" onClick={() => openOrder(null)} style={primaryBtn}>
              <Plus size={14} /> Nueva OP
            </button>
          }
          columns={[
            { key: 'Nº OP', sortKey: 'code' },
            { key: 'Solicitud', sortKey: 'request_code' },
            { key: 'Producto', sortKey: 'product_name' },
            { key: 'Fórmula', sortKey: 'formula_code' },
            { key: 'Plan vs real', sortKey: 'planned_qty', align: 'right' },
            { key: 'Fecha plan', sortKey: 'planned_date' },
            { key: 'Responsable', sortKey: 'responsible' },
            { key: 'Estado', sortKey: 'status', align: 'center' },
          ]}
          renderCells={(row) => (
            <>
              {cell(<strong>{row.code}</strong>)}
              {cell(row.request_code || '—')}
              {cell(`${row.product_code} · ${row.product_name}`)}
              {cell(row.formula_code || '—')}
              {cell(`${formatQty(row.planned_qty, row.unit)} / ${formatQty(row.actual_qty, row.unit)}`, 'right')}
              {cell(formatDateEs(row.planned_date))}
              {cell(row.responsible)}
              {cell(
                <ProductionStatusChip status={row.status} color={getBatchStatusColor(row.status)} />,
                'center'
              )}
            </>
          )}
        />
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
          Planificación
        </Heading>
        <p style={{ margin: 0, maxWidth: 720, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Recibe la demanda de producto y conviértela en órdenes de producción antes de liberar a planta.
        </p>
      </div>

      <TabNavigation tabs={tabs} defaultTab="requests" isDarkMode={isDarkMode} />

      <FormDrawer
        isOpen={detailFormOpen}
        onClose={() => !isSavingDetail && setDetailFormOpen(false)}
        onSave={saveDetail}
        isSaving={isSavingDetail}
        isDarkMode={isDarkMode}
        title="Reprogramar solicitud"
        subtitle={detailForm.code || 'Solicitud'}
        sections={detailSections}
        formData={detailForm}
        onChange={handleDetailChange}
        formError={formError}
        width={620}
        footerActions={[
          { key: 'cancel', label: 'Cancelar', onClick: () => setDetailFormOpen(false) },
          { key: 'save', label: 'Guardar fecha', variant: 'success', onClick: saveDetail },
        ]}
      />

      <ProductRequestViewModal
        isOpen={Boolean(viewRequest)}
        onClose={() => setViewRequestId(null)}
        request={viewRequest}
        items={viewRequestItems}
        isDarkMode={isDarkMode}
        canAccept={canAcceptRequest(viewRequest)}
        canReject={canRejectRequest(viewRequest)}
        canReschedule={canRescheduleRequest(viewRequest)}
        onAccept={acceptRequest}
        onReject={(row) => {
          setViewRequestId(null);
          rejectRequest(row);
        }}
        onReschedule={(row) => {
          setViewRequestId(null);
          openReschedule(row);
        }}
        onItemProgressChange={async (item, progressStatus) => {
          try {
            await updateProductRequestItemProgress(item.item_id || item.id, progressStatus);
          } catch (error) {
            window.alert(error?.message || 'No se pudo actualizar el avance del producto.');
          }
        }}
      />

      <ProductRequestRejectModal
        isOpen={Boolean(rejectRequestRow)}
        onClose={() => setRejectRequestRow(null)}
        onConfirm={confirmReject}
        request={rejectRequestRow}
        isDarkMode={isDarkMode}
      />

      <FormDrawer
        isOpen={orderFormOpen}
        onClose={() => setOrderFormOpen(false)}
        onSave={saveOrder}
        isDarkMode={isDarkMode}
        title="Nueva orden de producción"
        subtitle={orderForm.code || 'OP'}
        sections={orderSections}
        formData={orderForm}
        onChange={handleOrderChange}
        formError={formError}
        saveLabel="Crear OP"
        width={620}
      />
    </div>
  );
};

export default PaintPlanningWorkspace;
